import { range } from "@prismicio/editor-support/Array"
import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import {
  Badge,
  Box,
  Button,
  ComboBox,
  ComboBoxContent,
  ComboBoxInput,
  ComboBoxItem,
  ErrorBoundary,
  Icon,
  Skeleton,
  Text,
  useAlert,
} from "@prismicio/editor-ui"
import { Suspense, useEffect, useMemo, useRef, useState } from "react"

import {
  type EditorConfig,
  throwConfigError,
  useEditorConfig,
} from "../../../../../../EditorConfig"
import {
  type Tag,
  tagNameSchema,
  tagSchema,
  updateTagCache,
  useTagSearch,
} from "../../../../hooks/tagData"
import { useSelectedMedia } from "../../../../hooks/useSelectedMedia"
import { useMediaLibrary } from "../../../MediaLibraryContext"
import { useMediaLibraryToast } from "../../../MediaLibraryToast"

export type addOrRemove = "add" | "remove"

interface TagsProps {
  tags: Tag[]
  toggleAssetTag: (tag: Tag, action: addOrRemove) => Promise<void>
  getTagsLabel: () => string
}

export function Tags(props: TagsProps) {
  const { tags, toggleAssetTag, getTagsLabel } = props

  const { setTagManagerOpen } = useMediaLibrary()
  const { showToast } = useMediaLibraryToast()
  const { showAlert } = useAlert()

  const [tagSearchTerm, setTagSearchTerm] = useState("")

  async function onToggleAssetTag(tag: Tag, action: addOrRemove) {
    setTagSearchTerm("")
    try {
      await toggleAssetTag(tag, action)

      showToast({
        title: "Media tags were updated",
        seconds: 3,
      })
    } catch {
      showAlert({
        title: "Error",
        subtitle: "Something went wrong when updating media tags",
      })
    }
  }

  return (
    <Box flexDirection="column">
      <Box justifyContent="space-between">
        <Text color="grey12" variant="bold">
          {getTagsLabel()}
        </Text>
        <Button
          startIcon="edit"
          invisible
          color="dark"
          onClick={() => setTagManagerOpen(true)}
        >
          Manage tags
        </Button>
      </Box>
      <ComboBox sx={{ marginTop: 4, marginBottom: 8 }}>
        <TagInput searchTerm={tagSearchTerm} setSearchTerm={setTagSearchTerm} />
        <ComboBoxContent>
          <ErrorBoundary
            renderError={() => (
              <Text
                color="grey9"
                variant="small"
                sx={{ marginBlock: 8, marginInline: 8 }}
              >
                Error getting tags
              </Text>
            )}
          >
            <Suspense
              fallback={
                <Box padding={8} flexDirection="column" gap={8}>
                  {range(1, 6).map((num) => (
                    <Skeleton key={num} height={24} width={85} />
                  ))}
                </Box>
              }
            >
              <TagList
                searchTerm={tagSearchTerm}
                assignedTags={tags}
                onToggleAssetTag={onToggleAssetTag}
              />
            </Suspense>
          </ErrorBoundary>
        </ComboBoxContent>
      </ComboBox>
      <Box flexDirection="column" alignItems="flex-start" gap={8}>
        {tags.map((tag) => (
          <Badge
            key={tag.id}
            title={tag.name}
            size="medium"
            color="purple"
            selected
            onClose={() => onToggleAssetTag(tag, "remove")}
          />
        ))}
      </Box>
    </Box>
  )
}

interface TagInputProps {
  searchTerm: string
  setSearchTerm: (searchTerm: string) => void
}

function TagInput(props: TagInputProps) {
  const { searchTerm, setSearchTerm } = props

  const tagInputRef = useRef<HTMLInputElement>(null)

  const { openMediaSection, setOpenMediaSection } = useMediaLibrary()

  useEffect(() => {
    if (openMediaSection === "tags") {
      tagInputRef.current?.focus()
      setOpenMediaSection(undefined)
    }
  }, [openMediaSection, setOpenMediaSection])

  return (
    <ComboBoxInput
      value={searchTerm}
      onValueChange={setSearchTerm}
      placeholder="Add or create a new tag"
      ref={tagInputRef}
    />
  )
}

interface TagListProps {
  assignedTags: Tag[]
  searchTerm: string
  onToggleAssetTag: (tag: Tag, action: addOrRemove) => Promise<void>
}

function TagList(props: TagListProps) {
  const { searchTerm, assignedTags, onToggleAssetTag } = props

  const [media] = useSelectedMedia()
  const config = useEditorConfig()
  const { showAlert } = useAlert()

  const { tags } = useTagSearch()

  const filteredTags = useMemo(() => {
    const searchTermLowerCase = searchTerm.toLowerCase()
    return tags.filter((tag) =>
      tag.name.toLowerCase().includes(searchTermLowerCase),
    )
  }, [tags, searchTerm])

  const isEligibleForTagName = tagNameSchema.safeParse(searchTerm).success

  const isEligibleForNewTagName =
    isEligibleForTagName && tags.every((tag) => tag.name !== searchTerm)

  async function onCreateTag() {
    if (!searchTerm) return
    if (!media) return

    let newTag: Tag

    try {
      newTag = await createTag({ name: searchTerm, config })
      updateTagCache((tags) => [newTag, ...tags])
    } catch (_) {
      showAlert({
        title: "Error",
        subtitle: `Something went wrong when creating the tag ${searchTerm}`,
      })
      return
    }

    await onToggleAssetTag(newTag, "add")
  }

  if (!media) return null

  return (
    <>
      {!filteredTags.length && (
        <Text
          color="grey9"
          variant="small"
          sx={{ marginBlock: 8, marginInline: 8 }}
        >
          No tags found
        </Text>
      )}
      {filteredTags.map((tag, index) => (
        <ComboBoxItem
          key={tag.id}
          value={tag.id}
          onCheckedChange={(checked) =>
            onToggleAssetTag(tag, checked ? "add" : "remove")
          }
          checked={assignedTags.some((mediaTag) => mediaTag.id === tag.id)}
        >
          <Badge
            title={tag.name}
            color="purple"
            size="medium"
            sx={{
              marginInline: 8,
              marginBottom: index === filteredTags.length - 1 ? 8 : 4,
              marginTop: index === 0 ? 8 : 4,
            }}
          />
        </ComboBoxItem>
      ))}
      {isEligibleForNewTagName && (
        <ComboBoxItem value="new" onCheckedChange={onCreateTag}>
          <Box padding={8} flexDirection="row" alignItems="center" gap={4}>
            <Icon name="add" size="small" color="grey11" />
            <Text color="grey12">Create tag "{searchTerm}"</Text>
          </Box>
        </ComboBoxItem>
      )}
    </>
  )
}

interface CreateTagArgs {
  name: string
  config: EditorConfig
}

function createTag(args: CreateTagArgs) {
  const { name, config } = args

  if (!config.mediaLibrary) throwConfigError("mediaLibrary")

  const { assetsApiBaseUrl, repository, authStrategy } = config

  return request(new URL("tags", assetsApiBaseUrl), {
    headers: { repository },
    body: { name },
    method: "POST",
    credentials: getRequestCredentials(authStrategy),
    okType: tagSchema,
  })
}
